import { User, UserAction } from '../../dorian-shared/types/user/User';
import { auth } from '../Auth/Auth';

const WRITE_BOOK_ROLES = ['owner', 'editor', 'co-author'];

export function isAdminRole(user?: User | null) {
  const checkUser = user || auth.getUser();
  return checkUser?.role === 'admin';
}

export function isContentViewerRole(user?: User | null) {
  const checkUser = user || auth.getUser();
  return checkUser?.role === 'content_viewer';
}

export function canViewAllContent(user?: User | null) {
  const checkUser = user || auth.getUser();
  return isAdminRole(checkUser) || isContentViewerRole(checkUser);
}

export function canWriteBook(user?: User | null, bookRole?: string | null) {
  if (isAdminRole(user)) {
    return true;
  }
  return WRITE_BOOK_ROLES.includes(bookRole || '');
}

export function isForeignContentReadOnly(user?: User | null, bookRole?: string | null) {
  return isContentViewerRole(user) && !canWriteBook(user, bookRole);
}

export function isUserActionEnabled(user: User | undefined, action: UserAction) {
  const authUser = auth.getUser();

  const checkUser = user || authUser;

  if (isAdminRole(checkUser)) {
    return true;
  }
  return !!checkUser?.actions.includes(action);
}

export function isMemoryBankEnabled(user?: User) {
  return isUserActionEnabled(user, UserAction.ChoiceMemory);
}

export function isCharacterMemoriesEnabled(user?: User) {
  return isUserActionEnabled(user, UserAction.CharacterMemories);
}

export function isMemorySpeakersEnabled(user?: User) {
  return isUserActionEnabled(user, UserAction.MemorySpeakers);
}

export function isAchievementsEnabled(user?: User) {
  return isUserActionEnabled(user, UserAction.Achievements);
}

export function isVideosEnabled(user?: User) {
  return isUserActionEnabled(user, UserAction.Videos);
}

export function isCharactersEnabled(user?: User) {
  return isUserActionEnabled(user, UserAction.Characters);
}

export function isBackgroundsEnabled(user?: User) {
  return isUserActionEnabled(user, UserAction.Backgrounds);
}

export function isAudiosEnabled(user?: User) {
  return isUserActionEnabled(user, UserAction.Audios);
}

export function isABTestEnabled(user?: User) {
  return isUserActionEnabled(user, UserAction.ABTest);
}

export function isSocialLinksEnabled(user?: User) {
  return isUserActionEnabled(user, UserAction.SocialLinks);
}

export function isHideSavePointsEnabled(user?: User) {
  return isUserActionEnabled(user, UserAction.HideSavePoints);
}

export function isRenpyUploadEnabled(user?: User) {
  return isUserActionEnabled(user, UserAction.RenpyUpload);
}

export function isHubStepEnabled(user?: User) {
  return isUserActionEnabled(user, UserAction.HubStep);
}

/**
 * Whether the current user can see/edit Lasting Memory features.
 *
 * Three paths grant access (any of them):
 *  - admin role
 *  - per-user grant via `UserAction.LastingMemory`
 *  - global override via the `features.lastingMemoryEnabledForAll` Settings row
 *
 * The third path is the "flip after force-update" switch — admins set it to
 * `'true'` once legacy mobile clients have been upgraded out of the wild, at
 * which point every creator gets access without needing individual grants.
 *
 * `settings` is the result of `api.get('/v1/settings')`'s `data.settings`
 * shape (`{ section: { key: { value } } }`). Pass it in when available; calls
 * that don't have it loaded yet just don't get the global-override path.
 */
export function isLastingMemoryEnabled(
  user?: User,
  settings?: Record<string, Record<string, { value?: string }>>,
) {
  if (isUserActionEnabled(user, UserAction.LastingMemory)) {
    return true;
  }
  return settings?.features?.lastingMemoryEnabledForAll?.value === 'true';
}

/**
 * Whether the current user can see/use Bulk Character Import.
 *
 * Same OR-shaped gate as {@link isLastingMemoryEnabled}: admin role, or a
 * per-user `UserAction.BulkCharacterImport` grant, or the global
 * `features.bulkCharacterImportEnabledForAll` Settings row set to `'true'`. The
 * editor-api route mirrors this server-side. Pass `settings` from
 * `api.get('/v1/settings')`'s `data.settings`; without it, only the admin/grant
 * paths apply.
 */
export function isBulkCharacterImportEnabled(
  user?: User,
  settings?: Record<string, Record<string, { value?: string }>>,
) {
  if (isUserActionEnabled(user, UserAction.BulkCharacterImport)) {
    return true;
  }
  return settings?.features?.bulkCharacterImportEnabledForAll?.value === 'true';
}
